import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, Renderer2, OnDestroy, AfterViewInit, OnChanges, SimpleChanges, ChangeDetectorRef, HostBinding } from '@angular/core';
import { FormsModule } from '@angular/forms';

export interface DropdownOption {
  value: any;
  label: string;
  group?: string;
  disabled?: boolean;
  description?: string;
  icon?: string;
  bgColor?: string;
}

@Component({
  selector: 'app-dropdown-option',
  standalone: true,
  imports: [FormsModule, CommonModule],
  template: `
    <div class="relative w-full" #dropdownRoot>
      <!-- Trigger -->
      <div
        class="flex items-center gap-2 border-0 p-0 cursor-pointer trigger"
        [class.ticket-assignee-trigger]="appearance === 'ticket-assignee'"
        [class.w-fit]="!fullWidth"
        [class.min-w-[150px]]="!fullWidth"
        [class.w-full]="fullWidth"
        [class.min-w-0]="fullWidth"
        [style.minHeight.px]="44"
        (click)="toggleDropdown()"
        [class.opacity-50]="disabled"
        tabindex="0"
        (keydown.enter)="toggleDropdown()"
        (keydown.space)="toggleDropdown()"
        [attr.aria-expanded]="isOpen"
        [attr.aria-haspopup]="true"
      >
        <img *ngIf="iconSrc" [src]="iconSrc" alt="" class="w-5 h-5 flex-shrink-0">
        <span class="truncate text-gray-900 py-1 px-3 text-[13px] rounded trigger-label"
              [class.inline-block]="fullWidth"
              [class.max-w-full]="fullWidth"
              [class.has-bg-color]="appearance === 'default' && !!selectedOption?.bgColor"
              [class.ticket-assignee-label]="appearance === 'ticket-assignee'"
              *ngIf="selectedOption; else placeholderTpl"
              [attr.data-bg]="appearance === 'default' ? (selectedOption.bgColor || null) : null"
              [style.background]="appearance === 'default' ? (selectedOption.bgColor || '#f3f4f6') : 'transparent'"
              [title]="selectedOption?.label || ''">
          {{ getDisplayLabel(selectedOption.label) }}
        </span>
        <ng-template #placeholderTpl>
          <span class="flex-1 truncate text-gray-400 trigger-placeholder"
                [class.ticket-assignee-placeholder]="appearance === 'ticket-assignee'">
            {{ placeholder }}
          </span>
        </ng-template>
        <button
          *ngIf="clearable && selectedOption && appearance !== 'ticket-assignee'"
          class="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none clear-btn"
          (click)="clearSelection($event)"
          tabindex="-1"
          aria-label="Clear selection"
        >
          &#10005;
        </button>
      </div>

      <!-- Dropdown Menu -->
      <div
        *ngIf="isOpen"
        class="absolute max-h-64 overflow-auto min-w-40 rounded-xl left-0 w-full z-50 border bg-white border-gray-200 py-2 hide-scrollbar dropdown-menu"
        [class.ticket-assignee-menu]="appearance === 'ticket-assignee'"
        (click)="$event.stopPropagation()"
      >
        <div *ngIf="searchable" class="px-3 pb-2 dropdown-search-wrap"
             [class.ticket-assignee-search-wrap]="appearance === 'ticket-assignee'">
          <input
            #searchInput
            type="text"
            [(ngModel)]="searchTerm"
            (ngModelChange)="onSearchTermChange($event)"
            class="w-full border border-[#AAAAAA] focus:border-[#AAAAAA] rounded-lg px-2 py-1 text-sm dropdown-search-input"
            [class.ticket-assignee-search-input]="appearance === 'ticket-assignee'"
            [placeholder]="searchPlaceholder"
            (keydown.escape)="closeDropdown()"
          />
        </div>
        <div
          *ngIf="showSelectedOptionInMenu && selectedOption"
          class="selected-option-preview"
          [class.ticket-assignee-selected-option]="appearance === 'ticket-assignee'"
        >
          <span class="selected-option-preview-label" [title]="selectedOption.label">
            {{ getDisplayLabel(selectedOption.label) }}
          </span>
          <button
            *ngIf="clearable"
            type="button"
            class="selected-option-clear"
            (click)="clearSelection($event, false)"
            aria-label="Clear selection"
          >
            &#10005;
          </button>
        </div>
        <div class="w-full h-[1px] bg-gray-200"></div>
        <span class="dropdown-label px-2 " *ngIf="label">{{ label }}</span>
        <ng-container *ngIf="menuGroups.length > 0" class="min-w-[200px]">
          <ng-container *ngFor="let group of menuGroups; trackBy: trackByGroup">
            <div *ngIf="group.key"
                 class="px-4 py-1 text-xs text-gray-500 font-normal dropdown-group-label"
                 [class.ticket-assignee-group-label]="appearance === 'ticket-assignee'">
              {{ group.key }}
            </div>
            <ng-container *ngFor="let option of group.options; trackBy: trackByOption">
              <div
                class="flex items-center px-4 py-2 cursor-pointer hover:opacity-80 dropdown-item"
                [class.ticket-assignee-item]="appearance === 'ticket-assignee'"
                [class.text-gray-400]="option.disabled"
                (mousedown)="onOptionSelect($event, option)"
                (click)="selectOption(option)"
                [attr.aria-disabled]="option.disabled"
                [attr.tabindex]="option.disabled ? -1 : 0"
              >
                <span class="truncate text-sm font-normal py-1 px-2 rounded dropdown-item-label"
                      [class.flex-1]="appearance === 'ticket-assignee' || !option.bgColor"
                      [class.has-bg-color]="appearance === 'default' && !!option.bgColor"
                      [class.ticket-assignee-item-label]="appearance === 'ticket-assignee'"
                      [attr.data-bg]="appearance === 'default' ? (option.bgColor || null) : null"
                      [style.background]="appearance === 'default' ? (option.bgColor || 'transparent') : 'transparent'"
                      [title]="option.label">{{ getDisplayLabel(option.label) }}</span>
                <span *ngIf="isSelected(option) && !showSelectedOptionInMenu" class="ml-2 text-blue-600">&#10003;</span>
              </div>
            </ng-container>
          </ng-container>
        </ng-container>
        <div *ngIf="menuOptions.length === 0" class="px-4 py-2 text-gray-400 text-sm">No options</div>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }
    /* .min-h-44 { min-height: 44px; } */
    .rounded-xl { border-radius: 1rem; }
    .rounded-2xl { border-radius: 1.5rem; }
    .shadow-lg { box-shadow: 0 4px 24px 0 rgba(0,0,0,0.08); }
    .cursor-pointer { cursor: pointer; }
    .truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .text-gray-400 { color: #9ca3af; }
    .text-gray-900 { color: #111827; }
    .text-blue-600 { color: #2563eb; }
    .bg-gray-100 { background: #f3f4f6; }
    .bg-gray-50 { background: #f9fafb; }
    .bg-gray-200 { background: #e5e7eb; }
    .border-gray-200 { border-color: #e5e7eb; }
    .font-medium { font-weight: 500; }
    .text-xs { font-size: 0.75rem; }
    .text-sm { font-size: 0.875rem; }
    .w-full { width: 100%; }
    .px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
    .px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
    .px-4 { padding-left: 1rem; padding-right: 1rem; }
    .py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
    .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
    .ml-2 { margin-left: 0.5rem; }
    .flex { display: flex; }
    .items-center { align-items: center; }
    .relative { position: relative; }
    .absolute { position: absolute; }
    .left-0 { left: 0; }
    .mt-2 { margin-top: 0.5rem; }
    .z-50 { z-index: 50; }
    .opacity-50 { opacity: 0.5; }
    .clear-btn { display: none; }
    .trigger:hover .clear-btn,
    .trigger:focus-within .clear-btn {
      display: inline-block;
    }
  
    :host { display: block; }
    /* .min-h-44 { min-height: 44px; } */
    .rounded-xl { border-radius: 1rem; }
    .rounded-2xl { border-radius: 1.5rem; }
    .shadow-lg { box-shadow: 0 4px 24px 0 rgba(0,0,0,0.08); }
    .cursor-pointer { cursor: pointer; }
    .truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .text-gray-400 { color: #9ca3af; }
    .text-gray-900 { color: #111827; }
    .text-blue-600 { color: #2563eb; }
    .bg-gray-100 { background: #f3f4f6; }
    .bg-gray-50 { background: #f9fafb; }
    .border-gray-200 { border-color: #e5e7eb; }
    .font-medium { font-weight: 500; }
    .text-xs { font-size: 0.75rem; }
    .text-sm { font-size: 0.875rem; }
    .w-full { width: 100%; }
    .px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
    .px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
    .px-4 { padding-left: 1rem; padding-right: 1rem; }
    .py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
    .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
    .ml-2 { margin-left: 0.5rem; }
    .flex { display: flex; }
    .items-center { align-items: center; }
    .relative { position: relative; }
    .absolute { position: absolute; }
    .left-0 { left: 0; }
    .mt-2 { margin-top: 0.5rem; }
    .z-50 { z-index: 50; }
    .opacity-50 { opacity: 0.5; }
    .clear-btn { display: none; }
    .trigger:hover .clear-btn,
    .trigger:focus-within .clear-btn {
      display: inline-block;
    }
    .hide-scrollbar {
      scrollbar-width: none; /* Firefox */
      -ms-overflow-style: none; /* IE and Edge */
    }
    .hide-scrollbar::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
    .dropdown-menu {
      top: 100%;
      margin-top: 8px;
      z-index: 1200;
    }
    .dropdown-menu[style*='position: fixed'] {
      margin-top: 0 !important;
    }
    .dropdown-label{
      font-weight: 300;
      font-size:12px;
      color:#787878;
    }

    .ticket-assignee-trigger {
      min-height: 48px !important;
      gap: 10px;
      padding: 8px ;
      border-radius: 14px;
      background: #ffffff;
      // box-shadow: 0 1px 0 rgba(18, 18, 18, 0.02);
      transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
    }

    .ticket-assignee-trigger:hover {
      // border-color: #b8c4c9;
      // box-shadow: 0 8px 20px rgba(18, 18, 18, 0.06);
    }

    .ticket-assignee-trigger .trigger-label,
    .ticket-assignee-trigger .trigger-placeholder {
      flex: 1;
      padding: 0;
      background: transparent !important;
      font-family: 'Lexend', sans-serif;
      font-size: 16px;
      font-weight: 400;
      line-height: 1.3;
      color: #737373;
    }

    .ticket-assignee-trigger .ticket-assignee-placeholder {
      color: #9ca3af;
    }

    .ticket-assignee-menu {
      top: 100% !important;
      margin-top: 4px !important;
      max-width: min(420px, calc(100vw - 32px));
      max-height: 300px;
      min-width: 200px;
      padding: 4px;
      border-radius: 12px;
      background: #ffffff;
    }

    .ticket-assignee-search-wrap {
      padding: 0 0 10px 0;
    }

    .ticket-assignee-search-input {
          min-height: 40px;
    padding: 0 18px;
    border: 1px solid #d9dee2 !important;
    border-radius: 8px;
    background: #ffffff;
    font-family: "Lexend", sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #121212;
    }

    .ticket-assignee-search-input::placeholder {
      color: #9ca3af;
    }

    .ticket-assignee-search-input:focus {
      outline: none;
      // box-shadow: 0 0 0 3px rgba(35, 115, 178, 0.08);
    }

    .selected-option-preview {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 10px;
      padding: 7px 10px;
      border-radius: 8px;
      background: #f4f5f2;
      color: #2f3437;
    }

    .selected-option-preview-label {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-family: 'Lexend', sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: inherit;
    }

    .selected-option-clear {
      flex-shrink: 0;
      border: 0;
      background: transparent;
      color: #737373;
      font-size: 18px;
      line-height: 1;
      cursor: pointer;
      padding: 0;
    }

    .ticket-assignee-group-label {
      padding: 8px 14px 6px;
      font-family: 'Lexend', sans-serif;
      font-size: 13px;
      font-weight: 400;
      color: #8b9096;
      text-transform: none;
    }

    .ticket-assignee-item {
      min-height: 52px;
      padding: 0 14px;
      border-radius: 16px;
      transition: background-color 0.18s ease, color 0.18s ease;
    }

    .ticket-assignee-item:hover {
      background: #f7f8f6;
      opacity: 1 !important;
    }

    .ticket-assignee-item-label {
      padding: 0;
      font-family: 'Lexend', sans-serif;
      font-size: 16px;
      font-weight: 400;
      line-height: 1.3;
      color: #2f3437;
    }

    :host-context([data-theme='dark']) .ticket-assignee-trigger,
    :host-context(html.dark) .ticket-assignee-trigger {
      background: #1f1f1f !important;
      border: 1px solid #404040 !important;
    }

    :host-context([data-theme='dark']) .ticket-assignee-trigger img,
    :host-context(html.dark) .ticket-assignee-trigger img {
      filter: brightness(0) invert(1);
      opacity: 0.85;
    }

    :host-context([data-theme='dark']) .ticket-assignee-trigger .trigger-label,
    :host-context([data-theme='dark']) .ticket-assignee-trigger .trigger-placeholder,
    :host-context(html.dark) .ticket-assignee-trigger .trigger-label,
    :host-context(html.dark) .ticket-assignee-trigger .trigger-placeholder {
      color: #e5e7eb !important;
    }

    :host-context([data-theme='dark']) .ticket-assignee-trigger .ticket-assignee-placeholder,
    :host-context(html.dark) .ticket-assignee-trigger .ticket-assignee-placeholder {
      color: #9ca3af !important;
    }

    :host-context([data-theme='dark']) .ticket-assignee-menu,
    :host-context(html.dark) .ticket-assignee-menu {
      background: #1f1f1f !important;
      border-color: #404040 !important;
      box-shadow: 0 18px 40px rgba(0, 0, 0, 0.45);
    }

    :host-context([data-theme='dark']) .ticket-assignee-search-input,
    :host-context(html.dark) .ticket-assignee-search-input {
      background: #262626 !important;
      border-color: #525252 !important;
      color: #e5e7eb !important;
    }

    :host-context([data-theme='dark']) .ticket-assignee-search-input::placeholder,
    :host-context(html.dark) .ticket-assignee-search-input::placeholder {
      color: #8c8c8c !important;
    }

    :host-context([data-theme='dark']) .ticket-assignee-search-input:focus,
    :host-context(html.dark) .ticket-assignee-search-input:focus {
      border-color: #6b7280 !important;
      box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.05);
    }

    :host-context([data-theme='dark']) .ticket-assignee-selected-option,
    :host-context(html.dark) .ticket-assignee-selected-option {
      background: #262626 !important;
      border: 1px solid #404040 !important;
      color: #e5e7eb !important;
    }

    :host-context([data-theme='dark']) .ticket-assignee-selected-option .selected-option-preview-label,
    :host-context(html.dark) .ticket-assignee-selected-option .selected-option-preview-label {
      color: #e5e7eb !important;
    }

    :host-context([data-theme='dark']) .ticket-assignee-selected-option .selected-option-clear,
    :host-context(html.dark) .ticket-assignee-selected-option .selected-option-clear {
      color: #a3a3a3 !important;
    }

    :host-context([data-theme='dark']) .ticket-assignee-selected-option .selected-option-clear:hover,
    :host-context(html.dark) .ticket-assignee-selected-option .selected-option-clear:hover {
      color: #e5e7eb !important;
    }

    :host-context([data-theme='dark']) .ticket-assignee-group-label,
    :host-context(html.dark) .ticket-assignee-group-label,
    :host-context([data-theme='dark']) .dropdown-label,
    :host-context(html.dark) .dropdown-label {
      color: #9ca3af !important;
    }

    :host-context([data-theme='dark']) .ticket-assignee-item,
    :host-context(html.dark) .ticket-assignee-item {
      color: #e5e7eb !important;
    }

    :host-context([data-theme='dark']) .ticket-assignee-item:hover,
    :host-context(html.dark) .ticket-assignee-item:hover {
      background: #2a2a2a !important;
    }

    :host-context([data-theme='dark']) .ticket-assignee-item-label,
    :host-context(html.dark) .ticket-assignee-item-label {
      color: #e5e7eb !important;
    }

    :host-context([data-theme='dark']) .ticket-assignee-menu .bg-gray-200,
    :host-context(html.dark) .ticket-assignee-menu .bg-gray-200 {
      background: #404040 !important;
    }

    /* Dark mode */
    :host-context([data-theme='dark']) span.truncate:not(.text-gray-400):not(.has-bg-color),
    :host-context(html.dark) span.truncate:not(.text-gray-400):not(.has-bg-color) {
      background: none !important;
      color: #e5e5e5 !important;
    }

    :host-context([data-theme='dark']) span.truncate.has-bg-color,
    :host-context(html.dark) span.truncate.has-bg-color {
      background: none !important;
      color: #f3f4f6 !important;
      border: 1px solid #3f3f46;
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
    }

    :host-context([data-theme='dark']) span.truncate.has-bg-color[data-bg='#F7D7D3'],
    :host-context(html.dark) span.truncate.has-bg-color[data-bg='#F7D7D3'],
    :host-context([data-theme='dark']) span.truncate.has-bg-color[data-bg='#f7d7d3'],
    :host-context(html.dark) span.truncate.has-bg-color[data-bg='#f7d7d3'] {
      background: #5a1d1d !important;
      color: #ffaaaa !important;
      border-color: #8b3030 !important;
    }

    :host-context([data-theme='dark']) span.truncate.has-bg-color[data-bg='#FFE2C4'],
    :host-context(html.dark) span.truncate.has-bg-color[data-bg='#FFE2C4'],
    :host-context([data-theme='dark']) span.truncate.has-bg-color[data-bg='#ffe2c4'],
    :host-context(html.dark) span.truncate.has-bg-color[data-bg='#ffe2c4'] {
      background: #4a2d0a !important;
      color: #ffcc77 !important;
      border-color: #8b5c1a !important;
    }

    :host-context([data-theme='dark']) span.truncate.has-bg-color[data-bg='#D0FFD0'],
    :host-context(html.dark) span.truncate.has-bg-color[data-bg='#D0FFD0'],
    :host-context([data-theme='dark']) span.truncate.has-bg-color[data-bg='#d0ffd0'],
    :host-context(html.dark) span.truncate.has-bg-color[data-bg='#d0ffd0'] {
      background: #0d3b1f !important;
      color: #6ee89a !important;
      border-color: #1e7040 !important;
    }

    :host-context([data-theme='dark']) div.absolute,
    :host-context(html.dark) div.absolute {
      background: #1e1e1e !important;
      border-color: #404040 !important;
    }

    :host-context([data-theme='dark']) div.absolute div.flex:hover,
    :host-context(html.dark) div.absolute div.flex:hover {
      background: #2d2d2d !important;
    }

    :host-context([data-theme='dark']) div.absolute span,
    :host-context(html.dark) div.absolute span {
      color: #e5e5e5 !important;
    }

    :host-context([data-theme='dark']) div.absolute input,
    :host-context(html.dark) div.absolute input {
      background: #2d2d2d !important;
      border-color: #525252 !important;
      color: #e5e5e5 !important;
    }

    :host-context([data-theme='dark']) div.absolute div.text-gray-400,
    :host-context(html.dark) div.absolute div.text-gray-400 {
      color: #737373 !important;
    }

    /* Colored badges inside dropdown list - dark mode */
    :host-context([data-theme='dark']) div.absolute span.has-bg-color,
    :host-context(html.dark) div.absolute span.has-bg-color {
      background: rgba(100, 100, 100, 0.15) !important;
      color: #d4d4d4 !important;
      border: 1px solid rgba(100, 100, 100, 0.3) !important;
    }

    /* High priority */
    :host-context([data-theme='dark']) div.absolute span.has-bg-color[data-bg='#F7D7D3'],
    :host-context(html.dark) div.absolute span.has-bg-color[data-bg='#F7D7D3'],
    :host-context([data-theme='dark']) div.absolute span.has-bg-color[data-bg='#f7d7d3'],
    :host-context(html.dark) div.absolute span.has-bg-color[data-bg='#f7d7d3'] {
      background: #5a1d1d !important;
      color: #ffaaaa !important;
      border: 1px solid #8b3030 !important;
    }

    /* Medium priority */
    :host-context([data-theme='dark']) div.absolute span.has-bg-color[data-bg='#FFE2C4'],
    :host-context(html.dark) div.absolute span.has-bg-color[data-bg='#FFE2C4'],
    :host-context([data-theme='dark']) div.absolute span.has-bg-color[data-bg='#ffe2c4'],
    :host-context(html.dark) div.absolute span.has-bg-color[data-bg='#ffe2c4'] {
      background: #4a2d0a !important;
      color: #ffcc77 !important;
      border: 1px solid #8b5c1a !important;
    }

    /* Low priority */
    :host-context([data-theme='dark']) div.absolute span.has-bg-color[data-bg='#D0FFD0'],
    :host-context(html.dark) div.absolute span.has-bg-color[data-bg='#D0FFD0'],
    :host-context([data-theme='dark']) div.absolute span.has-bg-color[data-bg='#d0ffd0'],
    :host-context(html.dark) div.absolute span.has-bg-color[data-bg='#d0ffd0'] {
      background: #0d3b1f !important;
      color: #6ee89a !important;
      border: 1px solid #1e7040 !important;
    }
  `]
})
export class DropdownOptionComponent implements AfterViewInit, OnDestroy, OnChanges {
  @HostBinding('style.width') get hostWidth(): string | null {
    return this.fullWidth ? '100%' : null;
  }

  @Input() options: DropdownOption[] = [];
  @Input() placeholder: string = 'Select option';
  @Input() label: string = '';
  @Input() value: any;
  @Input() clearable: boolean = true;
  @Input() searchable: boolean = true;
  @Input() disabled: boolean = false;
  @Input() groupBy: ((option: DropdownOption) => string) | null = null;
  @Input() iconSrc: string = '';
  @Input() compareWith: ((a: any, b: any) => boolean) | null = null;
  @Input() enableTrim: boolean = false;
  @Input() maxLabelLength: number = 20;
  @Input() fullWidth: boolean = false;
  @Input() appearance: 'default' | 'ticket-assignee' = 'default';
  @Input() searchPlaceholder: string = 'Search...';
  @Input() showSelectedOptionInMenu: boolean = false;
  @Input() hideSelectedOptionFromList: boolean = false;

  @Output() valueChange = new EventEmitter<any>();

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dropdownRoot', { static: false }) dropdownRoot!: ElementRef;

  isOpen = false;
  searchTerm = '';
  menuOptions: DropdownOption[] = [];
  menuGroups: Array<{ key: string; options: DropdownOption[] }> = [];

  // Internal copy of options for change detection
  internalOptions: DropdownOption[] = [];

  private documentClickListener: (() => void) | null = null;

  constructor(private renderer: Renderer2, private hostElement: ElementRef, private cdr: ChangeDetectorRef) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options']) {
      // Create a new array reference to trigger change detection
      this.internalOptions = [...(this.options || [])];
    }

    if (changes['options'] || changes['value'] || changes['groupBy'] || changes['hideSelectedOptionFromList']) {
      this.rebuildMenuState();
      this.cdr.detectChanges();
    }
  }

  ngAfterViewInit() { }

  ngOnDestroy() {
    this.removeDocumentClickListener();
  }

  private addDocumentClickListener() {
    if (!this.documentClickListener) {
      this.documentClickListener = this.renderer.listen('document', 'mousedown', (event: MouseEvent) => {
        const root = this.dropdownRoot?.nativeElement || this.hostElement.nativeElement;
        if (this.isOpen && root && !root.contains(event.target)) {
          this.closeDropdown();
        }
      });
    }
  }

  private removeDocumentClickListener() {
    if (this.documentClickListener) {
      this.documentClickListener();
      this.documentClickListener = null;
    }
  }

  get selectedOption(): DropdownOption | null {
    const opts = this.internalOptions || this.options || [];
    if (!opts || opts.length === 0) return null;

    // Don't select anything if value is null, undefined, or empty string
    if (this.value === null || this.value === undefined || this.value === '') {
      return null;
    }

    return opts.find(opt => this.valuesEqual(opt.value, this.value)) || null;
  }

  private getFilteredOptions(): DropdownOption[] {
    let opts = this.internalOptions || this.options || [];
    if (this.searchable && this.searchTerm) {
      opts = opts.filter(opt => opt.label && opt.label.toLowerCase().includes(this.searchTerm.toLowerCase()));
    }
    return opts;
  }

  private getFilteredMenuOptions(): DropdownOption[] {
    const filtered = this.getFilteredOptions();
    if (!this.hideSelectedOptionFromList || !this.selectedOption) {
      return filtered;
    }

    return filtered.filter(opt => !this.valuesEqual(opt.value, this.selectedOption?.value));
  }

  private rebuildMenuState(): void {
    const filtered = this.getFilteredMenuOptions();
    this.menuOptions = filtered;

    if (!filtered || filtered.length === 0) {
      this.menuGroups = [];
      return;
    }

    if (this.groupBy) {
      const groupedMap = filtered.reduce((acc, opt) => {
        const groupKey = this.groupBy!(opt) || '';
        if (!acc.has(groupKey)) {
          acc.set(groupKey, []);
        }
        acc.get(groupKey)!.push(opt);
        return acc;
      }, new Map<string, DropdownOption[]>());

      this.menuGroups = Array.from(groupedMap.entries()).map(([key, options]) => ({ key, options }));
      return;
    }

    this.menuGroups = [{ key: '', options: filtered }];
  }

  toggleDropdown() {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.rebuildMenuState();
      this.addDocumentClickListener();
      if (this.searchable) {
        setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
      }
    } else {
      this.removeDocumentClickListener();
    }
  }

  closeDropdown() {
    this.removeDocumentClickListener();
    this.isOpen = false;
    this.searchTerm = '';
    this.rebuildMenuState();
  }

  selectOption(option: DropdownOption) {
    if (option.disabled) return;
    console.log('[DropdownOption] selectOption', {
      optionValue: option.value,
      optionValueType: typeof option.value,
      previousValue: this.value,
      previousValueType: typeof this.value,
      optionLabel: option.label
    });
    this.value = option.value;
    this.valueChange.emit(this.value);
    this.closeDropdown();
  }

  onOptionSelect(event: MouseEvent, option: DropdownOption) {
    event.preventDefault();
    event.stopPropagation();
    this.selectOption(option);
  }

  clearSelection(event: Event, shouldClose: boolean = true) {
    event.stopPropagation();
    this.value = null;
    this.valueChange.emit(this.value);
    if (shouldClose) {
      this.closeDropdown();
    } else {
      this.rebuildMenuState();
    }
  }

  onSearchTermChange(value: string): void {
    this.searchTerm = value;
    this.rebuildMenuState();
  }

  trackByGroup(index: number, group: { key: string; options: DropdownOption[] }): string {
    return `${group.key}-${index}`;
  }

  trackByOption(index: number, option: DropdownOption): any {
    return option.value ?? option.label ?? index;
  }

  isSelected(option: DropdownOption) {
    // Don't mark as selected if value is null, undefined, or empty string
    if (this.value === null || this.value === undefined || this.value === '') {
      return false;
    }
    // Don't mark as selected if option value is null, undefined, or empty string
    if (option.value === null || option.value === undefined || option.value === '') {
      return false;
    }
    return this.valuesEqual(this.value, option.value);
  }

  private valuesEqual(a: any, b: any): boolean {
    if (this.compareWith) {
      return this.compareWith(a, b);
    }
    if (a === b) return true;
    if (a === null || a === undefined || b === null || b === undefined) return false;
    // Handle numeric/string mismatches from forms/options
    return String(a) === String(b);
  }

  getDisplayLabel(label: string): string {
    if (!label) return '';
    if (!this.enableTrim) return label;
    return label.length > this.maxLabelLength ? `${label.substring(0, this.maxLabelLength)}...` : label;
  }
}
